import {
  FilterFn,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useRef, useState } from "react";
import { ScrollArea } from "@volca/ui/components/ui/scroll-area";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { fuzzyFilter } from "@volca/ui/lib/fuzzy-search";
import { cn } from "@volca/ui/lib/utils";
import { SimpleTableBodyRow } from "./body-row";
import { SimpleTableColGroup } from "./col-group";
import { SimpleTableFooter } from "./footer-row";
import { SimpleTableHeaderRows } from "./header-rows";
import { SimpleTableLoadingRows } from "./loading-rows";
import { getRowStripeIndex } from "./styles";
import { TablePagination } from "./table-pagination";
import type { SimpleTableProps } from "./types";
import { useTableScroll } from "./use-table-scroll";

export type { SimpleTableProps, SimpleTableRowVariant } from "./types";

const defaultColumnPinning = { right: ["actions"] };

export const SimpleTable = <TData,>({
  data,
  columns,
  className,
  tableClassName,
  scrollAreaClassName,
  scrollAreaHeightClassName,
  label,
  isLoading = false,
  emptyContent,
  loadingRowCount = 5,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  bottomContent,
  selectedRowId,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowId = (row) => (row as any).id,
  getRowVariant,
  scrollResetKey,
  viewportRef: externalViewportRef,
  onViewportScroll,
  searchValue,
  setSearchValue,
  manualSorting = false,
  rowDataCy,
  enablePagination = false,
  defaultPageSize = 25,
  manualPagination = false,
  pageCount,
  currentPage,
  totalRowCount,
  onPageChange,
  paginationState,
  onPaginationStateChange,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  disableMultiRowSelection = false,
  enableRowExpanding = false,
  expanded,
  onExpandedChange,
  getSubRows,
  columnPinning = defaultColumnPinning,
  enableStripedRows = false,
  increaseHeaderPadding,
  scrollContentStyle,
  respectColumnWidths = false,
  sorting: controlledSorting,
  onSortingChange,
}: SimpleTableProps<TData>) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const internalViewportRef = useRef<HTMLDivElement>(null);
  const viewportRef = externalViewportRef ?? internalViewportRef;
  const headerScrollRef = useRef<HTMLDivElement>(null);

  const [uncontrolledPagination, setUncontrolledPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const pagination = paginationState ?? uncontrolledPagination;

  useEffect(() => {
    if (
      !paginationState &&
      manualPagination &&
      currentPage != null &&
      currentPage !== uncontrolledPagination.pageIndex
    ) {
      setUncontrolledPagination((prev) => ({
        ...prev,
        pageIndex: currentPage,
      }));
    }
  }, [currentPage, manualPagination, paginationState, uncontrolledPagination]);

  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>(
    [],
  );
  const sorting = controlledSorting ?? uncontrolledSorting;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(!manualSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel() }),
    ...(enableRowExpanding && { getExpandedRowModel: getExpandedRowModel() }),
    filterFns: {
      fuzzy: fuzzyFilter as FilterFn<TData>,
    },
    globalFilterFn: fuzzyFilter as FilterFn<TData>,
    state: {
      globalFilter: searchValue?.trim(),
      sorting,
      columnPinning,
      ...(enablePagination && { pagination }),
      ...(enableRowSelection && rowSelection !== undefined && { rowSelection }),
      ...(enableRowExpanding && expanded !== undefined && { expanded }),
    },
    onGlobalFilterChange: setSearchValue,
    onSortingChange: (sortingUpdater) => {
      const next =
        typeof sortingUpdater === "function"
          ? sortingUpdater(sorting)
          : sortingUpdater;
      if (onSortingChange) onSortingChange(next);
      else setUncontrolledSorting(next);
    },
    ...(manualSorting && {
      manualSorting: true,
    }),
    ...(enablePagination && {
      onPaginationChange: (
        updater: Parameters<typeof setUncontrolledPagination>[0],
      ) => {
        const next =
          typeof updater === "function" ? updater(pagination) : updater;
        if (paginationState) {
          onPaginationStateChange?.(updater);
        } else {
          setUncontrolledPagination(next);
        }
        if (manualPagination) onPageChange?.(next.pageIndex);
      },
      manualPagination,
      ...(manualPagination && pageCount != null && { pageCount }),
    }),
    ...(enableRowSelection && {
      enableRowSelection: true,
      enableMultiRowSelection: !disableMultiRowSelection,
      onRowSelectionChange,
    }),
    ...(enableRowExpanding && {
      onExpandedChange,
      getSubRows,
    }),
    getRowId,
    enableColumnPinning: true,
  });

  useTableScroll({
    rootRef,
    viewportRef,
    headerScrollRef,
    scrollResetKey,
    selectedRowId,
    rowSelection,
    isLoading,
  });

  const colGroup = (
    <SimpleTableColGroup
      table={table}
      respectColumnWidths={respectColumnWidths}
    />
  );

  const tableClass = cn(
    "w-full caption-bottom text-sm",
    tableClassName,
    "table-fixed border-separate border-spacing-0",
  );

  const bodyRows = table.getRowModel().rows;
  const lastRowId = bodyRows[bodyRows.length - 1]?.id;
  const showEmptyContent =
    !isLoading && bodyRows.length === 0 && !!emptyContent;

  return (
    <Stack
      ref={rootRef}
      align="start"
      direction="column"
      gap={0}
      className={cn("flex h-full flex-col contain-[inline-size]", className)}
    >
      <div
        ref={headerScrollRef}
        className="w-full overflow-hidden contain-[inline-size]"
      >
        <table
          data-slot="table"
          className={tableClass}
          style={scrollContentStyle}
        >
          {colGroup}
          <TableHeader className="sticky top-0 [&_th]:border-b [&_tr]:border-b-0">
            <SimpleTableHeaderRows
              table={table}
              increaseHeaderPadding={increaseHeaderPadding}
            />
          </TableHeader>
        </table>
      </div>

      <ScrollArea
        horizontal
        viewportRef={viewportRef}
        viewportSlot="simple-table-viewport"
        viewportProps={{
          onScroll: onViewportScroll,
          tabIndex: 0,
          role: label ? "region" : undefined,
          "aria-label": label,
          "aria-busy": isLoading || undefined,
          className:
            "focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-focus/50",
        }}
        className={cn(
          "relative w-full overflow-hidden",
          scrollAreaHeightClassName ?? "flex-1",
          scrollAreaClassName,
        )}
      >
        <div
          className={cn("relative w-full", { "h-full": showEmptyContent })}
          style={showEmptyContent ? undefined : scrollContentStyle}
        >
          <table
            data-slot="table"
            className={cn(tableClass, { "h-full min-w-0": showEmptyContent })}
          >
            {!showEmptyContent && colGroup}
            <TableBody>
              {isLoading ? (
                <SimpleTableLoadingRows
                  columns={columns}
                  columnPinning={columnPinning}
                  loadingRowCount={loadingRowCount}
                />
              ) : bodyRows.length === 0 && emptyContent ? (
                <TableRow>
                  <TableCell
                    colSpan={table.getVisibleLeafColumns().length}
                    className="h-full p-0 [&>[data-slot=empty]]:h-full"
                  >
                    {emptyContent}
                  </TableCell>
                </TableRow>
              ) : (
                bodyRows.map((row) => (
                  <SimpleTableBodyRow
                    key={row.id}
                    row={row}
                    isStriped={
                      enableStripedRows &&
                      getRowStripeIndex(row, bodyRows) % 2 === 1
                    }
                    isLastRow={row.id === lastRowId}
                    isSelected={Boolean(
                      (selectedRowId &&
                        getRowId(row.original) === selectedRowId) ||
                        (rowSelection && rowSelection[row.id]),
                    )}
                    rowVariant={getRowVariant?.(row.original)}
                    rowDataCy={rowDataCy}
                    onRowClick={onRowClick}
                    onRowMouseEnter={onRowMouseEnter}
                    onRowMouseLeave={onRowMouseLeave}
                  />
                ))
              )}
            </TableBody>
            {!isLoading && bottomContent && (
              <SimpleTableFooter colSpan={table.getVisibleLeafColumns().length}>
                {bottomContent}
              </SimpleTableFooter>
            )}
          </table>
        </div>
      </ScrollArea>
      {enablePagination && (
        <TablePagination table={table} totalRowCount={totalRowCount} />
      )}
    </Stack>
  );
};
